/**
 * Card Carousel - Swiper Initialisation.
 *
 * Activates Swiper slider with custom responsive breakpoints,
 * navigation arrows, grab cursor, and accessibility support.
 */

import Swiper from 'swiper';
import { Navigation, Autoplay } from 'swiper/modules';
import 'swiper/css/bundle';

export const initCardCarousel = () => {
    const carousels = document.querySelectorAll('.card-carousel');
    if (!carousels.length) return;

    carousels.forEach((carousel) => {
        const swiperEl = carousel.querySelector('.cc-swiper');
        const btnPrev = carousel.querySelector('.cc-btn-prev');
        const btnNext = carousel.querySelector('.cc-btn-next');

        if (!swiperEl) return;
        if (swiperEl.swiper) return;

        const swiper = new Swiper(swiperEl, {
            modules: [Navigation, Autoplay],
            slidesPerView: 1,
            spaceBetween: 20,
            grabCursor: true,
            rewind: true,
            speed: 600,
            autoplay: {
                delay: 2500,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
            },
            navigation: {
                prevEl: btnPrev,
                nextEl: btnNext,
            },
            keyboard: {
                enabled: true,
                onlyInViewport: true,
            },
            a11y: {
                enabled: true,
                prevSlideMessage: 'Previous card',
                nextSlideMessage: 'Next card',
            },
            breakpoints: {
                576: {
                    slidesPerView: 1.5,
                    spaceBetween: 24,
                },
                768: {
                    slidesPerView: 2.1,
                    spaceBetween: 28,
                },
                1024: {
                    slidesPerView: 2.4,
                    spaceBetween: 32,
                },
                1280: {
                    slidesPerView: 2.6,
                    spaceBetween: 36,
                },
                1440: {
                    slidesPerView: 2.5,
                    spaceBetween: 40,
                },
            },
        });

        // Autoplay only when the component is within the viewport.
        if (swiper.autoplay) {
            swiper.autoplay.stop();

            if ('IntersectionObserver' in window) {
                const observer = new IntersectionObserver(
                    (entries) => {
                        entries.forEach((entry) => {
                            if (!swiper.autoplay) return;
                            if (entry.isIntersecting) {
                                swiper.autoplay.start();
                            } else {
                                swiper.autoplay.stop();
                            }
                        });
                    },
                    {
                        threshold: 0.15,
                    }
                );

                observer.observe(carousel);

                swiper.on('destroy', () => {
                    observer.disconnect();
                });
            } else {
                swiper.autoplay.start();
            }
        }
    });
};

// Self-initialise on DOMContentLoaded or immediately if DOM is already ready.
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCardCarousel);
} else {
    initCardCarousel();
}

// ACF Block Preview support in Gutenberg editor.
if (typeof window !== 'undefined' && window.acf) {
    window.acf.addAction(
        'render_block_preview/type=card-carousel',
        initCardCarousel
    );
}

export default initCardCarousel;